import { useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/News/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES } from 'pages/News/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import ComponentPreview from 'components/PreviewWrapper';
import { SERIALIZER_OPTIONS } from 'app/constants';
import { convertKeysToSnakeCase } from 'utils/helpers';
import DndWrapper from 'components/DndWrapper';
import ContentComponentsForm from 'components/Content/ContentComponentsForm';
import AddButton from 'components/AddButton';
import FormWrapper from 'components/FormWrapper';

function NewsCreate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  let debounce: ReturnType<typeof setTimeout>;

  const methods = useForm<News>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError, control } = methods;

  const {
    fields: containers,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'contentContainersAttributes',
    keyName: 'fieldId',
    shouldUnregister: true,
  });

  const addContainerHandler = useCallback(() => {
    append({ kind: '' } as ContentContainer);
  }, [append]);

  const reorderContainers = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      clearTimeout(debounce);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      debounce = setTimeout(() => move(dragIndex, hoverIndex), 500);
    },
    [move],
  );

  const { mutate: createNews, isLoading } = useMutation({
    mutationKey: 'newsMutation',
    mutationFn: (data: News) => {
      const snakePayload = convertKeysToSnakeCase(data);
      const formData = serialize(snakePayload, SERIALIZER_OPTIONS, new FormData(), 'news_item');
      return API.post(APIRoutes.news.index(), formData);
    },
    onSuccess: () => {
      toast.success('Artykuł został pomyślnie utworzony!');
      navigate(ROUTES.news.base);
      void queryClient.invalidateQueries('news');
    },
    onError: (error) => {
      setFormErrors(setError, error);
      toast.error('Wystąpił problem podczas tworzenia artykułu');
    },
  });

  const onSubmit = handleSubmit((data: News) => createNews(data));

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: 'Aktualności',
          to: ROUTES.news.base,
        }}
        crumbs={[{ to: '', label: 'Dodaj artykuł' }]}
      />
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="news-creator" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
            {containers.map((container, index) => (
              <DndWrapper
                key={`news-creator-${container.fieldId}`}
                id={container.fieldId}
                isLoading={isLoading}
                index={index}
                reorderContainers={reorderContainers}
                removeContainer={remove}
                ContainerWrapper={ContentComponentsForm}
                multipleContainers
              />
            ))}
            <AddButton addHandler={addContainerHandler} mt={2} />
          </Box>
          <ComponentPreview kind="news" />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} isCreate formId="news-creator" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default NewsCreate;
